import { motion } from "framer-motion";
import { X, Check } from "lucide-react";

const fadeIn = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

const BEFORE = [
    "The right people walk past your business and never notice.",
    "Ad quotes take days and come back written in agency-speak.",
    "You wonder if your last campaign actually reached anyone.",
    "Locking in a booking means chasing PDFs and phone calls.",
    "Small budgets get filtered out before a real conversation.",
];

const AFTER = [
    "The right people see your brand on the routes they already travel.",
    "Live prices, transparent tiers, and a plan in the time it takes to grab a coffee.",
    "A verified play log lands in your inbox — with estimated impressions from the operator's traffic model.",
    "Book, upload, go live — the whole flow moves through one wizard.",
    "Start from $250 in AUD and scale to city-wide when you're ready.",
];

/**
 * Before → After outcome comparison.
 * Positioned between UseCases and Process to bridge "this could help" → "I want this".
 * Every claim on this page must be defensible — see the trust rules in PRD.md.
 */
export const BeforeAfter = ({ onBegin }) => {
    return (
        <section
            id="outcomes"
            data-testid="landing-before-after"
            className="relative py-14 lg:py-20 border-t border-white/5"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeIn(0)} className="max-w-3xl mb-12">
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-6 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" />
                        04 / The change
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                        Two weeks from now,<br />
                        <span className="text-slate-500">a different picture.</span>
                    </h2>
                    <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
                        Most local advertisers describe the same before-state.
                        Here&apos;s what shifts when you book with NOXN.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/5 border border-white/5">
                    <motion.div {...fadeIn(0.05)} className="bg-[#0F1E3B] p-8 lg:p-10">
                        <div className="flex items-center gap-2 mb-5">
                            <span className="grid place-items-center w-6 h-6 bg-slate-800 border border-slate-700">
                                <X size={12} className="text-slate-400" />
                            </span>
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-500">
                                / Right now
                            </div>
                        </div>
                        <div className="font-display font-black text-2xl text-slate-300 mb-6 leading-snug">
                            Ads that go nowhere in particular
                        </div>
                        <ul className="space-y-4">
                            {BEFORE.map((line, i) => (
                                <li key={i} className="flex items-start gap-3 text-sm text-slate-400 leading-relaxed">
                                    <span className="mt-2 w-1 h-1 rounded-full bg-slate-600 shrink-0" />
                                    {line}
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    <motion.div {...fadeIn(0.12)} className="bg-gradient-to-br from-[#FF5A00]/10 via-[#0F1E3B] to-[#0F1E3B] p-8 lg:p-10">
                        <div className="flex items-center gap-2 mb-5">
                            <span className="grid place-items-center w-6 h-6 bg-[#FF5A00] border border-[#FF5A00]">
                                <Check size={12} className="text-white" />
                            </span>
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FF5A00]">
                                / With NOXN
                            </div>
                        </div>
                        <div className="font-display font-black text-2xl text-white mb-6 leading-snug">
                            A brand people notice on their daily routes
                        </div>
                        <ul className="space-y-4">
                            {AFTER.map((line, i) => (
                                <li key={i} className="flex items-start gap-3 text-sm text-slate-200 leading-relaxed">
                                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF5A00] shrink-0" />
                                    {line}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={onBegin}
                            data-testid="landing-outcomes-cta"
                            className="mt-8 inline-flex items-center gap-2 text-sm font-mono-tech uppercase tracking-widest text-white border-b border-[#FF5A00] pb-1 hover:gap-3 transition-all"
                        >
                            Plan my next two weeks <span aria-hidden>→</span>
                        </button>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
